import {
  deDE,
  enUS,
  esES,
  frFR,
  itIT,
  ptPT,
} from "@clerk/localizations";
import { normalizeToSupportedLanguage } from "./config";

const baseLocalizations = {
  fr: frFR,
  en: enUS,
  es: esES,
  de: deDE,
  pt: ptPT,
  it: itIT,
};

const customLocalizations = {
  en: {
    formFieldLabel__firstName: "First name",
    formFieldInputPlaceholder__firstName: "Your first name",
    formFieldLabel__lastName: "Last name",
    formFieldInputPlaceholder__lastName: "Your last name",
    formFieldLabel__password: "Password",
    formFieldInputPlaceholder__password: "Your password",
    formFieldLabel__passwordConfirm: "Confirm password",
    formFieldInputPlaceholder__passwordConfirm: "Type your password again",
    formFieldLabel__phoneNumber: "Mobile",
    formFieldInputPlaceholder__phoneNumber: "Your phone number",
    signIn: {
      start: {
        title: "Sign in to Qubiq",
        subtitle:
          "Sign in to play and earn qubs while watching your videos.",
        formFieldLabel__identifier: "Email or username",
        formFieldInputPlaceholder__identifier: "Enter your email or username",
        formFieldLabel__identifier_emailAddress_username: "Email or username",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Enter your email or username",
      },
      password: {
        title: "Enter your password",
        subtitle: "Provide the password linked to your account",
        actionLink: "Forgot your password?",
      },
      emailCode: {
        title: "Verify your email",
        subtitle: "We sent a code to your email address",
        formTitle: "Verification code",
        formSubtitle: "Enter the code to continue",
        resendButton: "Send code again",
      },
    },
    signUp: {
      start: {
        title: "Create your Qubiq account",
        subtitle: "Join the Qubiq Republic and start earning qubs.",
      },
      emailCode: {
        title: "Confirm your email",
        subtitle: "A code was sent to verify your address",
        formTitle: "Confirmation code",
        formSubtitle: "Enter the code below",
        resendButton: "Send code again",
      },
      continue: {
        title: "Additional information",
        subtitle: "Complete your registration",
      },
    },
  },
  fr: {
    userButton: {
      action__manageAccount: "Modifier mon profil",
    },
    formFieldLabel__firstName: "Prénom",
    formFieldInputPlaceholder__firstName: "Ton prénom",
    formFieldLabel__lastName: "Nom",
    formFieldInputPlaceholder__lastName: "Ton nom",
    formFieldLabel__password: "Mot de passe",
    formFieldInputPlaceholder__password: "Ton mot de passe",
    formFieldLabel__passwordConfirm: "Confirmer le mot de passe",
    formFieldInputPlaceholder__passwordConfirm: "Retape ton mot de passe",
    formFieldLabel__phoneNumber: "Mobile",
    formFieldInputPlaceholder__phoneNumber: "Ton numéro de téléphone",
    formButtonPrimary: "Continuer",
    formButtonPrimary__verify: "Vérifier",
    formFieldAction__forgotPassword: "Mot de passe oublié ?",
    reverification: {
      alternativeMethods: {
        actionLink: "Utiliser une autre méthode",
        actionText: "Tu n'arrives pas à accéder à ton compte ?",
        blockButton__backupCode: "Utiliser un code de récupération",
        blockButton__emailCode: "Recevoir un code par email",
        blockButton__passkey: "Utiliser une clé de sécurité",
        blockButton__password: "Utiliser ton mot de passe",
        blockButton__phoneCode: "Recevoir un code par téléphone",
        blockButton__totp: "Utiliser ton application d'authentification",
        subtitle: "Choisis une autre méthode pour vérifier ton identité.",
        title: "Vérification alternative",
      },
      backupCodeMfa: {
        subtitle: "Entre un de tes codes de récupération pour vérifier ton compte.",
        title: "Vérification par code de récupération",
      },
      emailCode: {
        formTitle: "Entre le code de vérification",
        resendButton: "Renvoyer le code",
        subtitle: "Un code a été envoyé à ton adresse email.",
        title: "Vérification par email",
      },
      noAvailableMethods: {
        message: "Aucune méthode de vérification n'est disponible.",
        subtitle: "Impossible de vérifier ton identité.",
        title: "Aucune méthode disponible",
      },
      passkey: {
        blockButton__passkey: "Utiliser ta clé de sécurité",
        subtitle: "Ta clé de sécurité permet de confirmer ton identité.",
        title: "Utiliser ta clé de sécurité",
      },
      password: {
        actionLink: "Réinitialiser ton mot de passe",
        subtitle: "Entre ton mot de passe pour continuer.",
        title: "Vérification par mot de passe",
      },
      phoneCode: {
        formTitle: "Entre le code de vérification",
        resendButton: "Renvoyer le code",
        subtitle: "Un code a été envoyé à ton téléphone.",
        title: "Vérification par téléphone",
      },
      phoneCodeMfa: {
        formTitle: "Entre le code de vérification",
        resendButton: "Renvoyer le code",
        subtitle: "Un code a été envoyé à ton téléphone pour vérifier ton identité.",
        title: "Vérification par téléphone",
      },
      totpMfa: {
        formTitle: "Entre le code de vérification",
        subtitle: "Entre le code généré par ton application d'authentification.",
        title: "Vérification par application d'authentification",
      },
    },
    signIn: {
      start: {
        title: "Connecte-toi sur Qubiq",
        subtitle: "",
        formFieldLabel__identifier: "Email ou pseudo",
        formFieldInputPlaceholder__identifier: "Entre ton email ou ton pseudo",
        formFieldLabel__identifier_emailAddress_username: "Email ou pseudo",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Entre ton email ou ton pseudo",
      },
      password: {
        title: "Entre ton mot de passe",
        subtitle: "Renseigne le mot de passe associé à ton compte",
        actionLink: "Mot de passe oublié ?",
      },
      emailCode: {
        title: "Vérifie ton email",
        subtitle: "Nous avons envoyé un code à ton adresse email",
        formTitle: "Code de vérification",
        formSubtitle: "Entre le code pour continuer",
        resendButton: "Renvoyer le code",
      },
    },
    signUp: {
      start: {
        title: "Crée ton compte Qubiq",
        subtitle: "",
      },
      emailCode: {
        title: "Confirme ton email",
        subtitle: "Un code t'a été envoyé pour vérifier ton adresse",
        formTitle: "Code de confirmation",
        formSubtitle: "Entre le code ci-dessous",
        resendButton: "Renvoyer le code",
      },
      continue: {
        title: "Informations complémentaires",
        subtitle: "Finalise ton inscription",
      },
    },
  },
  es: {
    formFieldLabel__firstName: "Nombre",
    formFieldInputPlaceholder__firstName: "Tu nombre",
    formFieldLabel__lastName: "Apellido",
    formFieldInputPlaceholder__lastName: "Tu apellido",
    formFieldLabel__password: "Contraseña",
    formFieldInputPlaceholder__password: "Tu contraseña",
    formFieldLabel__passwordConfirm: "Confirmar contraseña",
    formFieldInputPlaceholder__passwordConfirm: "Vuelve a escribir tu contraseña",
    formFieldLabel__phoneNumber: "Móvil",
    formFieldInputPlaceholder__phoneNumber: "Tu número de teléfono",
    signIn: {
      start: {
        title: "Inicia sesión en Qubiq",
        subtitle:
          "Inicia sesión para jugar y ganar qubs mientras ves tus vídeos.",
        formFieldLabel__identifier: "Email o usuario",
        formFieldInputPlaceholder__identifier: "Introduce tu email o usuario",
        formFieldLabel__identifier_emailAddress_username: "Email o usuario",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Introduce tu email o usuario",
      },
      password: {
        title: "Introduce tu contraseña",
        subtitle: "Introduce la contraseña asociada a tu cuenta",
        actionLink: "¿Has olvidado tu contraseña?",
      },
      emailCode: {
        title: "Verifica tu email",
        subtitle: "Hemos enviado un código a tu dirección de correo",
        formTitle: "Código de verificación",
        formSubtitle: "Introduce el código para continuar",
        resendButton: "Reenviar el código",
      },
    },
    signUp: {
      start: {
        title: "Crea tu cuenta de Qubiq",
        subtitle: "Únete a la Qubiq Republic y empieza a ganar qubs.",
      },
      emailCode: {
        title: "Confirma tu email",
        subtitle: "Se ha enviado un código para verificar tu dirección",
        formTitle: "Código de confirmación",
        formSubtitle: "Introduce el código a continuación",
        resendButton: "Reenviar el código",
      },
      continue: {
        title: "Información adicional",
        subtitle: "Completa tu registro",
      },
    },
  },
  de: {
    formFieldLabel__firstName: "Vorname",
    formFieldInputPlaceholder__firstName: "Dein Vorname",
    formFieldLabel__lastName: "Nachname",
    formFieldInputPlaceholder__lastName: "Dein Nachname",
    formFieldLabel__password: "Passwort",
    formFieldInputPlaceholder__password: "Dein Passwort",
    formFieldLabel__passwordConfirm: "Passwort bestätigen",
    formFieldInputPlaceholder__passwordConfirm: "Gib dein Passwort erneut ein",
    formFieldLabel__phoneNumber: "Mobil",
    formFieldInputPlaceholder__phoneNumber: "Deine Telefonnummer",
    signIn: {
      start: {
        title: "Melde dich bei Qubiq an",
        subtitle:
          "Melde dich an, um zu spielen und beim Anschauen deiner Videos Qubs zu verdienen.",
        formFieldLabel__identifier: "E-Mail oder Benutzername",
        formFieldInputPlaceholder__identifier:
          "Gib deine E-Mail oder deinen Benutzernamen ein",
        formFieldLabel__identifier_emailAddress_username:
          "E-Mail oder Benutzername",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Gib deine E-Mail oder deinen Benutzernamen ein",
      },
      password: {
        title: "Gib dein Passwort ein",
        subtitle: "Gib das mit deinem Konto verknüpfte Passwort ein",
        actionLink: "Passwort vergessen?",
      },
      emailCode: {
        title: "Bestätige deine E-Mail",
        subtitle: "Wir haben einen Code an deine E-Mail-Adresse gesendet",
        formTitle: "Bestätigungscode",
        formSubtitle: "Gib den Code ein, um fortzufahren",
        resendButton: "Code erneut senden",
      },
    },
    signUp: {
      start: {
        title: "Erstelle dein Qubiq-Konto",
        subtitle: "Tritt der Qubiq Republic bei und beginne, Qubs zu verdienen.",
      },
      emailCode: {
        title: "Bestätige deine E-Mail",
        subtitle: "Ein Code wurde gesendet, um deine Adresse zu bestätigen",
        formTitle: "Bestätigungscode",
        formSubtitle: "Gib den folgenden Code ein",
        resendButton: "Code erneut senden",
      },
      continue: {
        title: "Zusätzliche Informationen",
        subtitle: "Schließe deine Registrierung ab",
      },
    },
  },
  pt: {
    formFieldLabel__firstName: "Nome",
    formFieldInputPlaceholder__firstName: "O teu nome",
    formFieldLabel__lastName: "Apelido",
    formFieldInputPlaceholder__lastName: "O teu apelido",
    formFieldLabel__password: "Palavra-passe",
    formFieldInputPlaceholder__password: "A tua palavra-passe",
    formFieldLabel__passwordConfirm: "Confirmar palavra-passe",
    formFieldInputPlaceholder__passwordConfirm:
      "Volta a escrever a tua palavra-passe",
    formFieldLabel__phoneNumber: "Telemóvel",
    formFieldInputPlaceholder__phoneNumber: "O teu número de telefone",
    signIn: {
      start: {
        title: "Inicia sessão na Qubiq",
        subtitle:
          "Inicia sessão para jogar e ganhar qubs enquanto vês os teus vídeos.",
        formFieldLabel__identifier: "Email ou utilizador",
        formFieldInputPlaceholder__identifier:
          "Introduz o teu email ou utilizador",
        formFieldLabel__identifier_emailAddress_username:
          "Email ou utilizador",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Introduz o teu email ou utilizador",
      },
      password: {
        title: "Introduz a tua palavra-passe",
        subtitle: "Introduz a palavra-passe associada à tua conta",
        actionLink: "Esqueceste-te da palavra-passe?",
      },
      emailCode: {
        title: "Verifica o teu email",
        subtitle: "Enviámos um código para o teu endereço de email",
        formTitle: "Código de verificação",
        formSubtitle: "Introduz o código para continuar",
        resendButton: "Reenviar o código",
      },
    },
    signUp: {
      start: {
        title: "Cria a tua conta Qubiq",
        subtitle: "Junta-te à Qubiq Republic e começa a ganhar qubs.",
      },
      emailCode: {
        title: "Confirma o teu email",
        subtitle: "Foi enviado um código para verificar o teu endereço",
        formTitle: "Código de confirmação",
        formSubtitle: "Introduz o código abaixo",
        resendButton: "Reenviar o código",
      },
      continue: {
        title: "Informações adicionais",
        subtitle: "Conclui o teu registo",
      },
    },
  },
  it: {
    formFieldLabel__firstName: "Nome",
    formFieldInputPlaceholder__firstName: "Il tuo nome",
    formFieldLabel__lastName: "Cognome",
    formFieldInputPlaceholder__lastName: "Il tuo cognome",
    formFieldLabel__password: "Password",
    formFieldInputPlaceholder__password: "La tua password",
    formFieldLabel__passwordConfirm: "Conferma password",
    formFieldInputPlaceholder__passwordConfirm: "Inserisci di nuovo la password",
    formFieldLabel__phoneNumber: "Cellulare",
    formFieldInputPlaceholder__phoneNumber: "Il tuo numero di telefono",
    signIn: {
      start: {
        title: "Accedi a Qubiq",
        subtitle:
          "Accedi per giocare e guadagnare qubs mentre guardi i tuoi video.",
        formFieldLabel__identifier: "Email o username",
        formFieldInputPlaceholder__identifier:
          "Inserisci la tua email o username",
        formFieldLabel__identifier_emailAddress_username: "Email o username",
        formFieldInputPlaceholder__identifier_emailAddress_username:
          "Inserisci la tua email o username",
      },
      password: {
        title: "Inserisci la tua password",
        subtitle: "Inserisci la password associata al tuo account",
        actionLink: "Password dimenticata?",
      },
      emailCode: {
        title: "Verifica la tua email",
        subtitle: "Abbiamo inviato un codice al tuo indirizzo email",
        formTitle: "Codice di verifica",
        formSubtitle: "Inserisci il codice per continuare",
        resendButton: "Invia di nuovo il codice",
      },
    },
    signUp: {
      start: {
        title: "Crea il tuo account Qubiq",
        subtitle: "Unisciti alla Qubiq Republic e inizia a guadagnare qubs.",
      },
      emailCode: {
        title: "Conferma la tua email",
        subtitle: "È stato inviato un codice per verificare il tuo indirizzo",
        formTitle: "Codice di conferma",
        formSubtitle: "Inserisci il codice qui sotto",
        resendButton: "Invia di nuovo il codice",
      },
      continue: {
        title: "Informazioni aggiuntive",
        subtitle: "Completa la tua registrazione",
      },
    },
  },
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const mergeLocalizations = (base, overrides) => {
  if (!isPlainObject(base) || !isPlainObject(overrides)) {
    return overrides === undefined ? base : overrides;
  }

  const merged = { ...base };

  Object.entries(overrides).forEach(([key, value]) => {
    merged[key] = isPlainObject(value)
      ? mergeLocalizations(base[key], value)
      : value;
  });

  return merged;
};

export const getClerkLocalization = (language) => {
  const normalizedLanguage = normalizeToSupportedLanguage(language);
  const base = baseLocalizations[normalizedLanguage] || baseLocalizations.en;
  const overrides = customLocalizations[normalizedLanguage] || customLocalizations.en;

  return mergeLocalizations(base, overrides);
};
